import { DateTime } from 'luxon';
import { DeviceInfo, DeviceId } from '@capacitor/device';
import { Position } from '@capacitor/geolocation';
import { WithAccesRight } from '../utils/odoo';

// Pure Odoo API return type

type OdooPointingWithoutAccesRight = {
    id: number;
    date: string;
    real_time_start: string | false;
    real_time_end: string | false;
    input_time_start: string | false;
    input_time_end: string | false;
    lunch_night: number;
    travel: number;
    lunch: number;
    absence_hours: number;
    working_hours: number;
    working_night_hours: number;
    bad_weather_hours: number;
    employee_note: string;
    adherent_note: string;
    other_workplace: string;
    city: string;
    other_mad: string;
    state: string;
    employee_id: [number, string];
    mad_id: [number, string] | false;
    workplace_id: [number, string] | false;
    metadata: string;
    user_employee_validation_date: string | false;
    user_adherent_validation_date: string | false;
    user_adherent_id: [number, string] | false;
    user_geiq_validation_date: string | false;
    user_geiq_id: [number, string] | false;
    origin_id: [number, string] | false;
};

export type OdooPointing = WithAccesRight<OdooPointingWithoutAccesRight>;

export type OdooMad = {
    id: number;
    name: string;
    employee_id: [number, string];
    date_start: string;
    date_end: string;
    partner_id: [number, string];
};

export type OdooWorkPlace = {
    id: number;
    name: string;
    city: string;
    mad_ids: number[];
};

export type OdooCompany = {
    id: number;
    name: string;
    phone: string;
    email: string;
    pointing_rubric_absence_hours_id: [number, string] | false;
    pointing_rubric_working_hours_id: [number, string] | false;
    pointing_rubric_working_night_hours_ids: number[];
    pointing_rubric_travel_ids: number[];
    pointing_rubric_lunch_id: [number, string] | false;
    pointing_rubric_lunch_night_id: [number, string] | false;
    pointing_rubric_bad_weather_hours_id: [number, string] | false;
};

export type OdooEmployee = {
    id: number;
    name: string;
    company_id: [number, string];
    user_id: [number, string];
    best_phone: string;
    image_128: string;
    service: string;
    display_name: string;
};

export type OdooResourceCalendarAttendance = {
    id: number;
    calendar_id: [number, string];
    dayofweek: string;
    week_type: string | false;
};

export type OdooPublicHoliday = {
    id: number;
    date: string;
    name: string;
    zone: string;
    year: number;
};

export type OdooProvisionalCalendar = {
    id: number;
    employee_id: [number, string];
    mad_id: [number, string];
    workplace_id: [number, string];
    date_start: string;
    date_end: string;
    note: string;
    link_uuid: string;
};

export type OdooContract = {
    id: number;
    name: string;
    date_start: string;
    resource_calendar_id: [number, string];
};

export type OdooUser = {
    id: number;
    name: string;
    login: string;
    image_128: string;
};

// Model type transform from pure Odoo API return type

export type Mad = Omit<OdooMad, 'date_start' | 'date_end'> & {
    date_start: DateTime | null;
    date_end: DateTime | null;
    display_name: string;
};

export type OdooDomainCondition = [string, string, string | number | boolean | string[] | number[]];

export type OdooDomain = OdooDomainCondition[];

export type ProvisionalCalendar = Omit<OdooProvisionalCalendar, 'date_start' | 'date_end'> & {
    date_start: DateTime;
    date_end: DateTime;
};

export type ProvisionalWithRecurrence = Omit<
    OdooProvisionalCalendar,
    'date_start' | 'date_end' | 'employee_id' | 'mad_id' | 'workplace_id'
> & {
    date_start: DateTime;
    date_end: DateTime;
    employee_id: number;
    mad_id: number;
    workplace_id: number;
    is_recurrent: boolean;
    date_end_recurrent: DateTime;
    recurrence_type: 'day' | 'week' | null;
    is_application_global?: boolean;
};

export type ProvisionalCalendarToSaved = Omit<OdooProvisionalCalendar, 'employee_id' | 'mad_id' | 'workplace_id'> & {
    employee_id: number | false;
    mad_id: number | false;
    workplace_id: number | false;
};

export type MetaDataItem = {
    position: Position | null;
    deviceInfo: DeviceInfo | null;
    deviceID: DeviceId | null;
    ip: { wifi: any; carrier: any };
};

export type Pointing = Omit<
    OdooPointing,
    | 'real_time_start'
    | 'real_time_end'
    | 'input_time_start'
    | 'input_time_end'
    | 'employee_id'
    | 'mad_id'
    | 'workplace_id'
    | 'metadata'
    | 'user_employee_validation_date'
    | 'user_adherent_validation_date'
    | 'user_geiq_validation_date'
    | 'origin_id'
> & {
    real_time_start: DateTime | null;
    real_time_end: DateTime | null;
    input_time_start: DateTime | null;
    input_time_end: DateTime | null;
    employee_id: number;
    mad_id: number;
    workplace_id: number;
    metadata: {
        start: MetaDataItem | null;
        end: MetaDataItem | null;
        confirm: MetaDataItem | null;
    };
    user_employee_validation_date: DateTime | null;
    user_adherent_validation_date: DateTime | null;
    user_geiq_validation_date: DateTime | null;
    origin_id: number;
};

export type PointingToSaved = Omit<
    OdooPointingWithoutAccesRight,
    'employee_id' | 'mad_id' | 'workplace_id' | 'state' | 'origin_id' | 'user_adherent_id' | 'user_geiq_id'
> & {
    employee_id: number | false;
    mad_id: number | false;
    workplace_id: number | false;
    origin_id: number | false;
    user_adherent_id: number | false; // TODO: test can't edit with employee
    user_geiq_id: number | false;
};

export type PointingToSavePartial = Partial<PointingToSaved>;

export type GroupedPointingType = {
    date_count: number;
    'date:day': string;
};

type OdooLeaveWithoutAccesRight = {
    id: number;
    date_start: string;
    date_end: string;
    employee_note: string;
    adherent_note: string;
    geiq_note: string;
    state: string;
    mode: string;
    create_date: string;
    half_day: string;
    start_half_day: string;
    end_half_day: string;
    type: string;
    employee_id: [number, string];
    user_employee_validation_date: string | false;
    user_adherent_validation_date: string | false;
    user_adherent_id: [number, string] | false;
    user_geiq_validation_date: string | false;
    user_geiq_id: [number, string] | false;
};

export type OdooLeave = WithAccesRight<OdooLeaveWithoutAccesRight>;

export type Leave = Omit<
    OdooLeave,
    | 'name'
    | 'date_start'
    | 'date_end'
    | 'employee_id'
    | 'mode'
    | 'half_day'
    | 'start_half_day'
    | 'end_half_day'
    | 'metadata'
    | 'user_employee_validation_date'
    | 'user_adherent_validation_date'
    | 'user_geiq_validation_date'
> & {
    name: string;
    date_start: DateTime | null;
    date_end: DateTime | null;
    employee_id: number;
    mode: string;
    half_day: string;
    start_half_day: string;
    end_half_day: string;
    user_employee_validation_date: DateTime | null;
    user_adherent_validation_date: DateTime | null;
    user_geiq_validation_date: DateTime | null;
};

export type LeaveToSaved = Omit<
    OdooLeaveWithoutAccesRight,
    | 'name'
    | 'employee_id'
    | 'state'
    | 'type'
    | 'mode'
    | 'create_date'
    | 'half_day'
    | 'start_half_day'
    | 'end_half_day'
    | 'user_adherent_id'
    | 'user_geiq_id'
    | 'date_start'
    | 'date_end'
> & {
    name: string;
    employee_id: number | false;
    state: string;
    type: string;
    mode: string;
    date_start: string | null;
    date_end: string | null;
    create_date: string | null;
    half_day: string;
    start_half_day: string;
    end_half_day: string;
    user_adherent_id: number | false; // TODO: test can't edit with employee
    user_geiq_id: number | false;
    metadata: string;
};

// Snippets

export const pointingFields = [
    'id',
    'date',
    'employee_id',
    'real_time_start',
    'real_time_end',
    'input_time_start',
    'input_time_end',
    'lunch_night',
    'lunch',
    'mad_id',
    'employee_note',
    'adherent_note',
    'other_workplace',
    'city',
    'other_mad',
    'state',
    'travel',
    'absence_hours',
    'working_hours',
    'working_night_hours',
    'bad_weather_hours',
    'workplace_id',
    'metadata',
    'user_employee_validation_date',
    'user_adherent_validation_date',
    'user_adherent_id',
    'user_geiq_validation_date',
    'user_geiq_id',
    'origin_id',
] as const;

export const provisionalFields = [
    'id',
    'employee_id',
    'mad_id',
    'workplace_id',
    'date_start',
    'date_end',
    'note',
    'link_uuid',
] as const;

export const leaveFields = [
    'id',
    'employee_id',
    'date_start',
    'date_end',
    'employee_note',
    'adherent_note',
    'geiq_note',
    'state',
    'mode',
    'create_date',
    'half_day',
    'start_half_day',
    'end_half_day',
    'type',
    'user_employee_validation_date',
    'user_adherent_validation_date',
    'user_adherent_id',
    'user_geiq_validation_date',
    'user_geiq_id',
] as const;

export type OdooEmployeeLeave = {
    id: number;
    employee_id: [number, string];
    holiday_status_id: [number, string];
    request_date_from: string;
    request_date_to: string;
    request_unit_half: boolean;
    request_date_from_period: string;
};

export type OdooLeaveType = {
    id: number;
    name: string;
    calendar_color: string;
    calendar_abbreviation: string;
};
